import { useEffect, useMemo, useState } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { MRT_ColumnFiltersState as MRTColumnFiltersState } from "material-react-table";

// App state
import { RootState, useAttorneyHubDispatch } from "app/realtime-store/redux-store";

// Session
import { useSession } from "users/session/session-context";

// Domain contracts & values
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import Date from "common/values/date/date";

import PermissionFilterParameters from "permissions/entities/permission/api/request-contracts/permission-filter-parameters";
import PermissionOrderParameters from "permissions/entities/permission/api/request-contracts/permission-order-parameters";
import PermissionsQuery, { PermissionView } from "permissions/entities/permission/api/request-contracts/permission-query";
import { updatePermissionInterfaceQuery } from "permissions/entities/permission/store/permissions-redux-slice";

// Table types/utilities
import {
  ColumnFilter,
} from "permissions/entities/permission/view/components/permissions-table/types";
import PermissionTemplateQuery from "permissions/entities/permission/api/request-contracts/permission-template-query";
import { updatePermissionTemplateInterfaceQuery } from "permissions/entities/permission/store/permission-templates-redux-slice";

// Local types
export type UsePermissionsQuerySyncState = {
  columnFilters: MRTColumnFiltersState;
  setColumnFilters: React.Dispatch<React.SetStateAction<MRTColumnFiltersState>>;
  sorting: Array<{ id: string; desc: boolean }>;
  setSorting: React.Dispatch<React.SetStateAction<Array<{ id: string; desc: boolean }>>>;
  pagination: { pageIndex: number; pageSize: number };
  setPagination: React.Dispatch<React.SetStateAction<{ pageIndex: number; pageSize: number }>>;
};

/**
 * Centralizes URL <-> Redux query synchronization for the Permissions table.
 *
 * Responsibilities:
 * 1) Read URLSearchParams and dispatch updatePermissionInterfaceQuery with a PermissionsQuery
 * 2) Reflect column filter changes back into the URL
 * 3) Own table view state (sorting/pagination/columnFilters)
 */
export default function usePermissionsQuerySync(): UsePermissionsQuerySyncState {
  // Router & session
  const routeParams = useLoaderData();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();

  // Redux
  const dispatch = useAttorneyHubDispatch();
  const interfaceName = "permissionsTable";
  const templatesInterfaceName = "permissionTemplatesTable";
  const permissionsQuery = useSelector(
    (state: RootState) => state.permissions.interfaceQueries.entries[interfaceName]
  );
  const permissionTemplatesQuery = useSelector(
    (state: RootState) => state.permissionTemplates.interfaceQueries.entries[templatesInterfaceName]
  );

  // Table state
  const initialColumnFilters = useMemo(
    () => buildInitialColumnFilters(location.search),
    [location.search]
  );

  const [columnFilters, setColumnFilters] = useState<MRTColumnFiltersState>(initialColumnFilters);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
    pageIndex: 0,
    pageSize: 10,
  });

  function buildInitialColumnFilters(search: string): MRTColumnFiltersState {
    const params = new URLSearchParams(search);
    const filters: MRTColumnFiltersState = [];

    if (params.has("granteeUserId")) {
      filters.push({ id: "grantee", value: params.get("granteeUserId")! });
    }
    if (params.has("grantorUserId")) {
      filters.push({ id: "grantor", value: params.get("grantorUserId")! });
    }
    if (params.has("resource")) {
      filters.push({ id: "resource", value: params.get("resource")! });
    }
    if (params.has("name")) {
      filters.push({ id: "name", value: params.get("name")! });
    }

    const minCreatedDateString = params.get("minCreatedDate");
    const maxCreatedDateString = params.get("maxCreatedDate");
    if (minCreatedDateString || maxCreatedDateString) {
      filters.push({
        id: "created",
        value: [
          Date.fromJSON(minCreatedDateString)?.value,
          Date.fromJSON(maxCreatedDateString)?.value
        ]
      });
    }

    const minDisabledDateString = params.get("minDisabledDate");
    const maxDisabledDateString = params.get("maxDisabledDate");
    if (minDisabledDateString || maxDisabledDateString) {
      filters.push({
        id: "disabled",
        value: [
          Date.fromJSON(minDisabledDateString)?.value,
          Date.fromJSON(maxDisabledDateString)?.value
        ]
      });
    }

    return filters;
  }

  // Helper to build filter parameters from URLSearchParams
  function buildFilterParameters(query: URLSearchParams): PermissionFilterParameters {
    const filterParameters = new PermissionFilterParameters();
    filterParameters.showDisabled = true;

    // Grantee (single)
    const granteeUserIdString = query.get("granteeUserId");
    const granteeUserId = granteeUserIdString ? new Guid(granteeUserIdString) : undefined;
    if (granteeUserId && !granteeUserId.isEqualTo(Guid.empty)) {
      filterParameters.granteeUserIds = [granteeUserId];
    }

    // Grantee (multi)
    const granteeUserIds = query
      .getAll("granteeUserIds")
      .map((c) => new Guid(c.trim()))
      .filter(Boolean);
    if (granteeUserIds.length) {
      filterParameters.granteeUserIds = granteeUserIds;
    }

    // Grantor
    const grantorUserIdString = query.get("grantorUserId");
    const grantorUserId = grantorUserIdString ? new Guid(grantorUserIdString, true) : undefined;
    if (grantorUserId && !grantorUserId.isEqualTo(Guid.empty)) {
      filterParameters.grantorUserId = grantorUserId;
    }

    // Name
    const nameString = query.get("name");
    if (nameString) {
      filterParameters.name = nameString;
    }

    // Resource
    const resourceString = query.get("resource");
    if (resourceString) {
      filterParameters.resource = resourceString;
    }

    // Created date range
    const minCreatedDateString = query.get("minCreatedDate");
    const maxCreatedDateString = query.get("maxCreatedDate");
    if (minCreatedDateString || maxCreatedDateString) {
      filterParameters.minCreatedDate = Date.fromJSON(minCreatedDateString) ?? undefined;
      filterParameters.maxCreatedDate = Date.fromJSON(maxCreatedDateString) ?? undefined;
    }

    // Disabled date range
    const minDisabledDateString = query.get("minDisabledDate");
    const maxDisabledDateString = query.get("maxDisabledDate");
    if (minDisabledDateString || maxDisabledDateString) {
      filterParameters.minDisabledDate = Date.fromJSON(minDisabledDateString) ?? undefined;
      filterParameters.maxDisabledDate = Date.fromJSON(maxDisabledDateString) ?? undefined;
    }

    // Has parent flag
    const hasParentString = query.get("hasParent");
    if (hasParentString !== null) {
      filterParameters.hasParent = hasParentString === "true";
    }

    // Categories (multi)
    const urlCategories = query
      .getAll("categories")
      .map((c) => c.trim())
      .filter(Boolean);
    if (urlCategories.length) {
      filterParameters.categories = urlCategories;
    }

    // Access Type (enum as string)
    const urlAccessType = query.get("accessType");
    if (urlAccessType) {
      filterParameters.accessType = urlAccessType.trim();
    }

    return filterParameters;
  }

  // Helper to build order parameters from sorting
  function buildOrderParameters(sorting: Array<{ id: string; desc: boolean }>): PermissionOrderParameters {
    let orderBy: "CreatedDate" | "DisabledDate" | "Resource" | undefined;
    if (sorting.length > 0 && sorting[0].id === "created") {
      orderBy = "CreatedDate";
    }
    if (sorting.length > 0 && sorting[0].id === "resource") {
      orderBy = "Resource";
    }
    if (sorting.length > 0 && sorting[0].id === "disabled") {
      orderBy = "DisabledDate";
    }

    return new PermissionOrderParameters(
      orderBy,
      sorting.length > 0 && !sorting[0].desc ? OrderDirection.Ascending : OrderDirection.Descending
    );
  }

  // Build Redux permissions query from URL + view state
  useEffect(() => {
    if (!session.user?.companyEntityId) return;

    const query = new URLSearchParams(location.search);
    const filterParameters = buildFilterParameters(query);
    const orderParameters = buildOrderParameters(sorting);
    const paginationParams = new PaginationParameters(
      pagination.pageIndex,
      pagination.pageSize
    );

    const newQuery = new PermissionsQuery(
      filterParameters, 
      orderParameters, 
      paginationParams,
      PermissionView.Grantee,
      3
    );
    
    if (newQuery.isEqualTo(permissionsQuery)) return;
    
    dispatch(
      updatePermissionInterfaceQuery({
        interfaceName,
        query: newQuery,
      })
    );
    
    const newTemplatesQuery = new PermissionTemplateQuery(
      filterParameters,
      orderParameters,
      paginationParams
    );

    if (newTemplatesQuery.isEqualTo(permissionTemplatesQuery)) return;

    dispatch(
      updatePermissionTemplateInterfaceQuery({
        interfaceName: templatesInterfaceName,
        query: newTemplatesQuery,
      })
    );
  }, [
    routeParams,
    location,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    session?.user?.companyEntityId,
  ]);

  // Reflect column filter changes -> URLSearchParams
  useEffect(() => {
    const prev = new URLSearchParams(location.search);
    const next = new URLSearchParams(location.search);

    const setKeyIfFilter = (
      filter: ColumnFilter | undefined,
      key: string,
      mapValue: (v: unknown) => string | null = (v) => {
        if (v == null) return null;
        if (v instanceof Date) return v.toISOString();
        if (typeof v === "object") return JSON.stringify(v);
        if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
        return JSON.stringify(v);
      }
    ) => {
      if (!filter) return;
      const mapped = mapValue(filter.value);
      if (mapped === null || mapped === "") next.delete(key);
      else next.set(key, mapped);
    };

    // Grantee
    setKeyIfFilter(columnFilters.find((f) => f.id === "grantee"), "granteeUserId");

    // Grantor
    setKeyIfFilter(columnFilters.find((f) => f.id === "grantor"), "grantorUserId");

    // Resource
    setKeyIfFilter(columnFilters.find((f) => f.id === "resource"), "resource");

    // Created range
    const createdFilter: ColumnFilter | undefined = columnFilters.find((f) => f.id === "created");
    if (createdFilter) {
      const createdValue = createdFilter.value as [Date | undefined, Date | undefined] | undefined;
      const minCreatedDate: Date | undefined = createdValue?.[0];
      const maxCreatedDate: Date | undefined = createdValue?.[1];
      if (minCreatedDate) next.set("minCreatedDate", minCreatedDate.toISOString()); else next.delete("minCreatedDate");
      if (maxCreatedDate) next.set("maxCreatedDate", maxCreatedDate.toISOString()); else next.delete("maxCreatedDate");
    }

    // Disabled range
    const disabledFilter: ColumnFilter | undefined = columnFilters.find((f) => f.id === "disabled");
    if (disabledFilter) {
      const disabledValue = disabledFilter.value as [Date | undefined, Date | undefined] | undefined;
      const minDisabledDate: Date | undefined = disabledValue?.[0];
      const maxDisabledDate: Date | undefined = disabledValue?.[1];
      if (minDisabledDate) next.set("minDisabledDate", minDisabledDate.toISOString()); else next.delete("minDisabledDate");
      if (maxDisabledDate) next.set("maxDisabledDate", maxDisabledDate.toISOString()); else next.delete("maxDisabledDate");
    }

    const prevStr = prev.toString();
    const nextStr = next.toString();
    if (prevStr !== nextStr) navigate({ search: nextStr });
  }, [columnFilters, location.search, navigate]);

  return {
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
    pagination,
    setPagination,
  };
}
